import {AiChatGrafico} from '../../../_models/ai-chat.model';

// Feature 160: dal grafico calcolato da AgentApi alla configurazione chart.js, alle tessere, alle righe di
// avanzamento e ai dati copiabili. Funzioni pure, testabili senza componente.

export interface ColoriGrafico {
  serie: string[];
  testo: string;
  griglia: string;
}

/** Configurazione chart.js (tipo, dati, opzioni): tipizzata qui per non legare il modulo ai generici di chart.js. */
export interface ConfigurazioneChart {
  type: 'bar' | 'line' | 'doughnut';
  data: {labels: string[]; datasets: Record<string, unknown>[]};
  options: Record<string, unknown>;
}

export interface Tessera {
  etichetta: string;
  valore: string;
}

export interface RigaAvanzamento {
  etichetta: string;
  testo: string;
  percentuale: number;
  percentualeScarti: number;
}

const TIPI_CANVAS = ['barre', 'barreOrizzontali', 'linea', 'ciambella'];
const NUMERO = new Intl.NumberFormat('it-IT', {maximumFractionDigits: 2});

export function usaCanvas(grafico: AiChatGrafico): boolean {
  return TIPI_CANVAS.includes(grafico.tipo);
}

export function formattaValore(valore: number | null | undefined, unita?: string | null): string {
  if (valore === null || valore === undefined) return '—';
  return unita ? `${NUMERO.format(valore)} ${unita}` : NUMERO.format(valore);
}

/** Tabella separata da tabulazioni, con intestazione: si incolla in Excel. */
export function tsv(grafico: AiChatGrafico): string {
  const intestazione = ['', ...grafico.serie.map(s => s.nome)].join('\t');
  const righe = grafico.etichette.map((etichetta, i) =>
    [etichetta, ...grafico.serie.map(s => s.valori[i] ?? '')].join('\t'));
  return [intestazione, ...righe].join('\n');
}

/** Nome del file PNG dal titolo, senza caratteri non ammessi nei nomi di file. */
export function nomeFile(grafico: AiChatGrafico): string {
  const base = grafico.titolo.normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  return `${base || 'grafico'}.png`;
}

export function tessere(grafico: AiChatGrafico): Tessera[] {
  const valori = grafico.serie[0]?.valori ?? [];
  return grafico.etichette.map((etichetta, i) => ({etichetta, valore: formattaValore(valori[i], grafico.unita)}));
}

/** Serie nell'ordine fatto, richiesto, scarti (contracts/mostra-grafico.md). */
export function righeAvanzamento(grafico: AiChatGrafico): RigaAvanzamento[] {
  const [fatto, richiesto, scarti] = grafico.serie;
  return grafico.etichette.map((etichetta, i) => {
    const f = fatto?.valori[i] ?? 0;
    const r = richiesto?.valori[i] ?? 0;
    const s = scarti?.valori[i] ?? 0;
    const testoScarti = scarti ? ` · ${formattaValore(s)} scarti` : '';
    return {etichetta, testo: `${formattaValore(f)} / ${formattaValore(r, grafico.unita)}${testoScarti}`,
      percentuale: percentuale(f, r), percentualeScarti: percentuale(s, r)};
  });
}

function percentuale(parte: number, totale: number): number {
  return totale > 0 ? Math.min(100, Math.round(parte / totale * 100)) : 0;
}

export function configurazione(grafico: AiChatGrafico, colori: ColoriGrafico): ConfigurazioneChart {
  const ciambella = grafico.tipo === 'ciambella';
  return {
    type: ciambella ? 'doughnut' : grafico.tipo === 'linea' ? 'line' : 'bar',
    data: {labels: grafico.etichette, datasets: grafico.serie.map((s, i) => dataset(grafico, s.nome, s.valori, i, colori))},
    options: {
      maintainAspectRatio: false,
      animation: false,
      indexAxis: grafico.tipo === 'barreOrizzontali' ? 'y' : 'x',
      plugins: {
        legend: {display: ciambella || grafico.serie.length > 1, position: 'bottom', labels: {color: colori.testo, boxWidth: 12}},
        tooltip: {callbacks: {label: (c: TooltipContesto) => `${c.dataset.label}: ${formattaValore(c.raw, grafico.unita)}`}}
      },
      scales: ciambella ? {} : assi(grafico, colori)
    }
  };
}

interface TooltipContesto {
  dataset: {label?: string};
  raw: number | null;
}

function dataset(grafico: AiChatGrafico, nome: string, valori: (number | null)[], i: number, colori: ColoriGrafico) {
  const colore = colori.serie[i % colori.serie.length];
  const fette = grafico.tipo === 'ciambella' ? valori.map((_, j) => colori.serie[j % colori.serie.length]) : colore;
  return {label: nome, data: valori, backgroundColor: fette, borderColor: grafico.tipo === 'linea' ? colore : undefined,
    borderRadius: grafico.tipo.startsWith('barre') ? 4 : 0, spanGaps: false, tension: 0.2};
}

function assi(grafico: AiChatGrafico, colori: ColoriGrafico) {
  const asse = {stacked: !!grafico.impilate, ticks: {color: colori.testo}, grid: {color: colori.griglia}};
  return {x: {...asse}, y: {...asse, beginAtZero: true}};
}
